"use client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAnalysisVotes } from "@/hooks/use-analysis-votes";
import { trackClientError } from "@/lib/client-error";
import { cn, formatCompactNumber } from "@/lib/utils";
import { ArrowBigDown, ArrowBigUp, Home } from "lucide-react";
import { toast } from "../ui/use-toast";

interface PropertyVoteCardProps {
  analyzedPropertyId: string;
  className?: string;
  initialVoteCount: number;
  initialUserVote: number;
  masterkeyScore?: number;
}

export function PropertyVoteCard({
  analyzedPropertyId,
  className,
  initialVoteCount,
  initialUserVote,
  masterkeyScore = 0,
}: PropertyVoteCardProps) {
  const { voteCount, userVote, isVoting, handleVote } = useAnalysisVotes({
    analyzedPropertyId,
    initialVoteCount,
    initialUserVote,
  });

  const onVote = async (direction: "up" | "down") => {
    try {
      await handleVote(direction);
    } catch (error) {
      trackClientError(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Calculate total score by adding masterkey score to vote count
  const totalScore = masterkeyScore + voteCount;

  return (
    <Card
      className={cn(
        "relative overflow-hidden bg-black/70 backdrop-blur-md border-2 border-white/20 hover:bg-black/75 transition-all duration-300",
        "before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/10 before:to-transparent before:pointer-events-none",
        className,
      )}
    >
      <div className="relative flex flex-col items-center justify-between h-full py-2.5">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "rounded-full w-9 h-9 p-0 transition-all duration-200",
            "bg-white/10 hover:bg-emerald-500/30 hover:text-emerald-300",
            "active:scale-95",
            userVote === 1 &&
              "bg-emerald-500/30 text-emerald-300 ring-1 ring-emerald-500/40",
            isVoting && "opacity-50 cursor-not-allowed",
            "text-white shadow-sm",
          )}
          onClick={() => onVote("up")}
          disabled={isVoting}
        >
          <ArrowBigUp className="h-5 w-5" strokeWidth={2.5} />
        </Button>

        <div className="flex flex-col items-center py-1.5">
          <div className="flex items-center gap-1 mb-0.5">
            <Home className="w-3 h-3 text-white/80" />
            <span className="text-[10px] uppercase tracking-wider text-white/80 font-medium">
              Score
            </span>
          </div>
          <span
            className={cn(
              "font-bold text-2xl transition-all duration-200 tabular-nums leading-none",
              totalScore > masterkeyScore
                ? "text-emerald-300"
                : totalScore < masterkeyScore
                  ? "text-red-300"
                  : "text-white",
            )}
          >
            {formatCompactNumber(totalScore)}
          </span>
        </div>

        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "rounded-full w-9 h-9 p-0 transition-all duration-200",
            "bg-white/10 hover:bg-red-500/30 hover:text-red-300",
            "active:scale-95",
            userVote === -1 &&
              "bg-red-500/30 text-red-300 ring-1 ring-red-500/40",
            isVoting && "opacity-50 cursor-not-allowed",
            "text-white shadow-sm",
          )}
          onClick={() => onVote("down")}
          disabled={isVoting}
        >
          <ArrowBigDown className="h-5 w-5" strokeWidth={2.5} />
        </Button>
      </div>
    </Card>
  );
}
